import type { SubLedger, SubLedgerType, SystemYear, Supplier, FinancialsSupplierAddress, Project, CostCentre, Currency, NominalAccount, AssetLookupText } from '@/types/financials';
import { getAuthConfig } from '@/lib/auth-fetch';
import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { getApiUrl } from '@/lib/api-config';

// Mock data for demo mode (reuse the same mock data as Financials)
const mockSubLedgers: SubLedger[] = [
  { id: '1', code: 'SL001', description: 'General Ledger', type: 'Nominal' },
  { id: '2', code: 'SL002', description: 'Fixed Assets', type: 'Nominal' },
  { id: '3', code: 'SL003', description: 'Inventory', type: 'Nominal' },
  { id: '4', code: 'SL004', description: 'Accounts Payable', type: 'Purchase' },
  { id: '5', code: 'SL005', description: 'Accounts Receivable', type: 'Sales' },
];

const mockSystemYears: SystemYear[] = [
  { id: '1', label: '2025', startDate: '2025-01-01', endDate: '2025-12-31', nominal: 2025 },
  { id: '2', label: '2024', startDate: '2024-01-01', endDate: '2024-12-31', nominal: 2024 },
  { id: '3', label: '2023', startDate: '2023-01-01', endDate: '2023-12-31', nominal: 2023 },
  { id: '4', label: '2022', startDate: '2022-01-01', endDate: '2022-12-31', nominal: 2022 },
  { id: '5', label: '2021', startDate: '2021-01-01', endDate: '2021-12-31', nominal: 2021 },
];

const mockSuppliers: Supplier[] = [
  { primaryKey: 1, code: 'SUP001', name: 'Acme Corporation', inactive: false, balance: 15000.00, currencyCode: 'GBP' },
  { primaryKey: 2, code: 'SUP002', name: 'Tech Solutions Ltd', inactive: false, balance: 8500.50, currencyCode: 'GBP' },
  { primaryKey: 3, code: 'SUP003', name: 'Office Supplies Inc', inactive: false, balance: 2300.00, currencyCode: 'GBP' },
  { primaryKey: 4, code: 'SUP004', name: 'Industrial Equipment Co', inactive: false, balance: 45000.00, currencyCode: 'GBP' },
  { primaryKey: 5, code: 'SUP005', name: 'Global Logistics', inactive: true, balance: 0, currencyCode: 'GBP' },
  { primaryKey: 6, code: 'SUP006', name: 'Premium Parts Ltd', inactive: false, balance: 12750.25, currencyCode: 'GBP' },
];

const mockSupplierAddresses: Record<string, FinancialsSupplierAddress[]> = {
  'SUP001': [
    {
      summarySection: { primaryKey: 1, number: 1, accountCode: 'SUP001', isOrder: true, isRemittance: false, isDelivery: true, completeContactName: 'John Smith' },
      details: { address: '123 Main Street', town: 'London', county: 'Greater London', country: 'UK', postCode: 'EC1A 1BB', telephone: '020 1234 5678', email: 'orders@acme.com' },
    },
    {
      summarySection: { primaryKey: 2, number: 2, accountCode: 'SUP001', isOrder: false, isRemittance: true, isDelivery: false, completeContactName: 'Finance Dept' },
      details: { address: '456 Finance Road', town: 'Manchester', county: 'Greater Manchester', country: 'UK', postCode: 'M1 1AA', telephone: '0161 234 5678', email: 'finance@acme.com' },
    },
  ],
  'SUP002': [
    {
      summarySection: { primaryKey: 3, number: 1, accountCode: 'SUP002', isOrder: true, isRemittance: true, isDelivery: true, completeContactName: 'Sarah Johnson' },
      details: { address: '789 Tech Park', town: 'Cambridge', county: 'Cambridgeshire', country: 'UK', postCode: 'CB1 2AB', telephone: '01onal 345 6789', email: 'info@techsolutions.com' },
    },
  ],
};

const mockCostCentres: CostCentre[] = [
  { code: 'CC001', name: 'Head Office' },
  { code: 'CC002', name: 'Manufacturing' },
  { code: 'CC003', name: 'Sales & Marketing' },
  { code: 'CC004', name: 'IT Department' },
  { code: 'CC005', name: 'Finance' },
];

const mockProjects: Project[] = [
  { code: 'PRJ001', name: 'Office Renovation' },
  { code: 'PRJ002', name: 'IT Infrastructure Upgrade' },
  { code: 'PRJ003', name: 'New Product Launch' },
  { code: 'PRJ004', name: 'Marketing Campaign 2025' },
  { code: 'PRJ005', name: 'Warehouse Expansion' },
];

const mockCurrencies: Currency[] = [
  { primaryKey: 1, symbol: '£', description: 'British Pound', isoCode: 'GBP', isEuroCurrency: false, currencyType: 'Home' },
  { primaryKey: 2, symbol: '$', description: 'US Dollar', isoCode: 'USD', isEuroCurrency: false, currencyType: 'Foreign' },
  { primaryKey: 3, symbol: '€', description: 'Euro', isoCode: 'EUR', isEuroCurrency: true, currencyType: 'Foreign' },
];

const mockNominalAccounts: NominalAccount[] = [
  { id: '1', code: '1000', name: 'Fixed Assets - Cost', doNotUse: false, currencyCode: 'GBP' },
  { id: '2', code: '1001', name: 'Fixed Assets - Accumulated Depreciation', doNotUse: false, currencyCode: 'GBP' },
  { id: '3', code: '1100', name: 'Motor Vehicles - Cost', doNotUse: false, currencyCode: 'GBP' },
  { id: '4', code: '1101', name: 'Motor Vehicles - Accumulated Depreciation', doNotUse: false, currencyCode: 'GBP' },
  { id: '5', code: '1200', name: 'Office Equipment - Cost', doNotUse: false, currencyCode: 'GBP' },
  { id: '6', code: '1201', name: 'Office Equipment - Accumulated Depreciation', doNotUse: false, currencyCode: 'GBP' },
  { id: '7', code: '6000', name: 'Depreciation Expense', doNotUse: false, currencyCode: 'GBP' },
  { id: '8', code: '6100', name: 'Depreciation - Motor Vehicles', doNotUse: false, currencyCode: 'GBP' },
  { id: '9', code: '6200', name: 'Depreciation - Office Equipment', doNotUse: false, currencyCode: 'GBP' },
  { id: '10', code: '7000', name: 'Disposal of Assets', doNotUse: false, currencyCode: 'GBP' },
  { id: '11', code: '7100', name: 'Profit/Loss on Disposal', doNotUse: false, currencyCode: 'GBP' },
  { id: '12', code: '1300', name: 'Bank Account', doNotUse: false, currencyCode: 'GBP' },
  { id: '13', code: '1400', name: 'Work in Progress', doNotUse: false, currencyCode: 'GBP' },
  { id: '14', code: '3000', name: 'Revaluation Reserve', doNotUse: false, currencyCode: 'GBP' },
  { id: '15', code: '3100', name: 'Revaluation Surplus', doNotUse: false, currencyCode: 'GBP' },
  { id: '16', code: '9999', name: 'Inactive Account', doNotUse: true, currencyCode: 'GBP' },
];

const mockAssetLookupText: Record<string, AssetLookupText[]> = {
  'ASSORT1': [
    { id: '1', origin: 'ASSORT1', text: 'Department A', sortOrder: 1 },
    { id: '2', origin: 'ASSORT1', text: 'Department B', sortOrder: 2 },
    { id: '3', origin: 'ASSORT1', text: 'Department C', sortOrder: 3 },
  ],
  'ASSORT2': [
    { id: '4', origin: 'ASSORT2', text: 'Building 1', sortOrder: 1 },
    { id: '5', origin: 'ASSORT2', text: 'Building 2', sortOrder: 2 },
    { id: '6', origin: 'ASSORT2', text: 'Building 3', sortOrder: 3 },
  ],
  'ASSORT3': [
    { id: '7', origin: 'ASSORT3', text: 'Project Alpha', sortOrder: 1 },
    { id: '8', origin: 'ASSORT3', text: 'Project Beta', sortOrder: 2 },
    { id: '9', origin: 'ASSORT3', text: 'Project Gamma', sortOrder: 3 },
  ],
  'ASSORT4': [
    { id: '10', origin: 'ASSORT4', text: 'Cost Center 100', sortOrder: 1 },
    { id: '11', origin: 'ASSORT4', text: 'Cost Center 200', sortOrder: 2 },
    { id: '12', origin: 'ASSORT4', text: 'Cost Center 300', sortOrder: 3 },
  ],
  'ASSORT5': [
    { id: '13', origin: 'ASSORT5', text: 'Region North', sortOrder: 1 },
    { id: '14', origin: 'ASSORT5', text: 'Region South', sortOrder: 2 },
    { id: '15', origin: 'ASSORT5', text: 'Region East', sortOrder: 3 },
    { id: '16', origin: 'ASSORT5', text: 'Region West', sortOrder: 4 },
  ],
};

const getDataSource = (): 'demo' | 'api' => {
  const mode = localStorage.getItem('app-mode');
  if (mode === 'demo') return 'demo';
  return 'api';
};

class DimensionsGraphQLService {
  private getBackendApiBaseUrl(): string {
    const apiUrlFromConfig = getApiUrl();
    const envUrl = import.meta.env.VITE_API_URL;
    const backendUrl = getBackendUrl();
    const isE2B = typeof window !== 'undefined' && window.location.hostname.includes('.e2b.app');

    const baseUrl = isE2B ? backendUrl : (apiUrlFromConfig ?? envUrl ?? backendUrl);

    return baseUrl.replace(/\/$/, '');
  }

  private async fetchBackendAPI<T>(endpoint: string): Promise<T> {
    const baseUrl = this.getBackendApiBaseUrl();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const authConfig = getAuthConfig();

    if (authConfig.getAccessToken) {
      try {
        const token = await authConfig.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    }

    const instanceId = authConfig.instanceId ?? 'default-instance';
    headers['X-Instance-Id'] = instanceId;

    try {
      const response = await fetch(`${baseUrl}${endpoint}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        let errorMessage = `API error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if ((errorData as { message?: string }).message) {
            errorMessage = (errorData as { message: string }).message;
          }
        } catch {
          // Ignore JSON parse errors
        }
        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Unable to connect to API at ${baseUrl}. Please ensure the backend server is running or switch to Demo mode.`);
      }
      throw error;
    }
  }

  async getSubLedgers(type?: SubLedgerType): Promise<SubLedger[]> {
    if (getDataSource() === 'demo') {
      if (type) {
        return mockSubLedgers.filter(sl => sl.type === type);
      }
      return mockSubLedgers;
    }

    try {
      const params = type ? `?type=${encodeURIComponent(type)}` : '';
      const data = await this.fetchBackendAPI<SubLedger[]>(`/api/dimensions/sub-ledgers${params}`);
      return data;
    } catch (error) {
      console.error('Failed to fetch sub ledgers from Dimensions backend API:', error);
      throw error;
    }
  }

  async getSystemYears(sort?: string): Promise<SystemYear[]> {
    if (getDataSource() === 'demo') {
      const years = [...mockSystemYears];
      if (sort === 'desc') {
        return years.sort((a, b) => b.nominal - a.nominal);
      } else if (sort === 'asc') {
        return years.sort((a, b) => a.nominal - b.nominal);
      }
      return years;
    }

    try {
      const params = sort ? `?sort=${encodeURIComponent(sort)}` : '';
      const data = await this.fetchBackendAPI<SystemYear[]>(`/api/dimensions/system-years${params}`);
      return data;
    } catch (error) {
      console.error('Failed to fetch system years from Dimensions backend API:', error);
      throw error;
    }
  }

  async getSuppliers(includeInactive?: boolean): Promise<Supplier[]> {
    if (getDataSource() === 'demo') {
      if (includeInactive) {
        return mockSuppliers;
      }
      return mockSuppliers.filter(s => !s.inactive);
    }

    try {
      const params = includeInactive ? '?includeInactive=true' : '';
      const data = await this.fetchBackendAPI<Supplier[]>(`/api/dimensions/suppliers${params}`);
      return data;
    } catch (error) {
      console.error('Failed to fetch suppliers from Dimensions backend API:', error);
      throw error;
    }
  }

  async getSupplierAddresses(supplierCode: string): Promise<FinancialsSupplierAddress[]> {
    if (getDataSource() === 'demo') {
      return mockSupplierAddresses[supplierCode] ?? [];
    }

    try {
      const params = `?supplierCode=${encodeURIComponent(supplierCode)}`;
      const data = await this.fetchBackendAPI<FinancialsSupplierAddress[]>(`/api/dimensions/supplier-addresses${params}`);
      return data;
    } catch (error) {
      console.error('Failed to fetch supplier addresses from Dimensions backend API:', error);
      throw error;
    }
  }

  async getProjects(): Promise<Project[]> {
    if (getDataSource() === 'demo') {
      return mockProjects;
    }

    try {
      const data = await this.fetchBackendAPI<Project[]>('/api/dimensions/projects');
      return data;
    } catch (error) {
      console.error('Failed to fetch projects from Dimensions backend API:', error);
      throw error;
    }
  }

  async getCostCentres(): Promise<CostCentre[]> {
    if (getDataSource() === 'demo') {
      return mockCostCentres;
    }

    try {
      const data = await this.fetchBackendAPI<CostCentre[]>('/api/dimensions/cost-centres');
      return data;
    } catch (error) {
      console.error('Failed to fetch cost centres from Dimensions backend API:', error);
      throw error;
    }
  }

  async getCurrencies(): Promise<Currency[]> {
    if (getDataSource() === 'demo') {
      return mockCurrencies;
    }

    try {
      const data = await this.fetchBackendAPI<Currency[]>('/api/dimensions/currencies');
      return data;
    } catch (error) {
      console.error('Failed to fetch currencies from Dimensions backend API:', error);
      throw error;
    }
  }

  async getNominalAccounts(currencyCode?: string): Promise<NominalAccount[]> {
    if (getDataSource() === 'demo') {
      if (currencyCode) {
        return mockNominalAccounts.filter(a => a.currencyCode === currencyCode);
      }
      return mockNominalAccounts;
    }

    try {
      const params = currencyCode ? `?currencyCode=${encodeURIComponent(currencyCode)}` : '';
      const data = await this.fetchBackendAPI<NominalAccount[]>(`/api/dimensions/nominal-accounts${params}`);
      return data;
    } catch (error) {
      console.error('Failed to fetch nominal accounts from Dimensions backend API:', error);
      throw error;
    }
  }

  async getAssetLookupText(origin: string): Promise<AssetLookupText[]> {
    if (getDataSource() === 'demo') {
      return mockAssetLookupText[origin] ?? [];
    }

    try {
      const params = `?origin=${encodeURIComponent(origin)}`;
      const data = await this.fetchBackendAPI<AssetLookupText[]>(`/api/dimensions/asset-lookup-text${params}`);
      return data;
    } catch (error) {
      console.error('Failed to fetch asset lookup text from Dimensions backend API:', error);
      throw error;
    }
  }
}

export const dimensionsGraphQLService = new DimensionsGraphQLService();

export const dimensionsService = dimensionsGraphQLService;
